import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useCountry } from "../lib/country";
import { useLanguage } from "../lib/language";
import { ShoppingBag, User, Menu, X, BadgeCheck, LogOut, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { getPublicName } from "../lib/publicName";

export default function Navbar() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    // country context still exists but is hard-coded to SV — we keep the
    // import in case future callers need it, but no UI switcher is exposed.
    useCountry();
    const { t } = useLanguage();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [logo, setLogo] = useState("");

    useEffect(() => {
        api.get("/settings").then(({ data }) => setLogo(data.logo || ""));
    }, []);

    // Handle Emergent Google auth redirect (#session_id=...)
    const { googleLogin } = useAuth();
    useEffect(() => {
        if (window.location.hash.includes("session_id=")) {
            const sid = window.location.hash.split("session_id=")[1].split("&")[0];
            googleLogin(sid).then(() => {
                window.history.replaceState(null, "", window.location.pathname);
                nav("/");
            }).catch(console.error);
        }
        // eslint-disable-next-line
    }, []);

    const FEATURED_CATEGORIES = {
        women: [
            { label: t("Perfumes Mujer", "Women's Perfume"), category: "Perfumes Mujer" },
            { label: t("Ropa Mujer", "Women's Clothing"), category: "Ropa Mujer" },
            { label: t("Calzado Mujer", "Women's Shoes"), category: "Calzado Mujer" },
            { label: t("Bolsos Mujer", "Women's Bags"), category: "Bolsos Mujer" },
            { label: t("Joyería Mujer", "Women's Jewelry"), category: "Joyería Mujer" },
            { label: t("Maquillaje", "Makeup"), category: "Maquillaje Mujer" },
        ],
        men: [
            { label: t("Perfumes Hombre", "Men's Cologne"), category: "Perfumes Hombre" },
            { label: t("Ropa Hombre", "Men's Clothing"), category: "Ropa Hombre" },
            { label: t("Calzado Hombre", "Men's Shoes"), category: "Calzado Hombre" },
            { label: t("Accesorios Hombre", "Men's Accessories"), category: "Accesorios Hombre" },
            { label: t("Joyería Hombre", "Men's Jewelry"), category: "Joyería Hombre" },
        ],
    };
    const [shopMenu, setShopMenu] = useState(false);

    const links = [
        { to: "/", label: t("Inicio", "Home") },
        { to: "/solicitar-producto", label: t("Solicitar", "Request") },
        { to: "/vendedores", label: t("Vendedores", "Sellers") },
        { to: "/tracking", label: t("Rastrear", "Track") },
        { to: "/reviews", label: t("Reseñas", "Reviews") },
        { to: "/como-usar", label: t("Cómo Usar", "How to use") },
        { to: "/soporte", label: t("Soporte", "Support") },
    ];

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-[var(--tb-bg)]/80 border-b border-[var(--tb-border)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
                <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink" data-testid="nav-logo">
                    {logo ? <img src={logo} alt="" className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 object-cover rounded-full border border-[var(--tb-gold)] flex-shrink-0" /> :
                        <div className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded-full bg-[var(--tb-gold)] flex items-center justify-center text-white font-bold font-display text-base md:text-xl flex-shrink-0">T</div>}
                    <div className="min-w-0">
                        <div className="font-display text-base sm:text-lg md:text-2xl tracking-tight leading-none whitespace-nowrap truncate">Terra Boutique</div>
                        <div className="tb-overline text-[var(--tb-gold)] text-[9px] sm:text-[10px] mt-0.5 sm:mt-1">El Salvador</div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
                    <Link to="/" className={`tb-link text-sm tracking-wide whitespace-nowrap ${loc.pathname === "/" ? "text-[var(--tb-gold)]" : ""}`} data-testid="nav-link-home">
                        {t("Inicio", "Home")}
                    </Link>
                    {/* Tienda dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShopMenu(!shopMenu)}
                            className={`tb-link text-sm tracking-wide inline-flex items-center gap-1 ${loc.pathname === "/tienda" ? "text-[var(--tb-gold)]" : ""}`}
                            data-testid="nav-link-tienda">
                            {t("Tienda", "Shop")} <ChevronDown className={`w-3 h-3 transition-transform ${shopMenu ? "rotate-180" : ""}`} />
                        </button>
                        {shopMenu && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setShopMenu(false)} />
                                <div className="absolute left-1/2 -translate-x-1/2 mt-3 w-[28rem] bg-[var(--tb-bg)] border border-[var(--tb-border-gold)]/40 rounded shadow-2xl z-50 grid grid-cols-2 gap-0 overflow-hidden" data-testid="shop-dropdown">
                                    <div className="border-r border-[var(--tb-border)]">
                                        <Link
                                            to="/tienda"
                                            onClick={() => setShopMenu(false)}
                                            className="block p-3 border-b border-[var(--tb-border)] text-sm font-display text-[var(--tb-gold)] hover:bg-[var(--tb-bg-2)]"
                                            data-testid="shop-cat-women">
                                            {t("♀ Mujeres", "♀ Women")}
                                        </Link>
                                        {FEATURED_CATEGORIES.women.map((c) => (
                                            <Link
                                                key={c.category}
                                                to={`/tienda?categoria=${encodeURIComponent(c.category)}`}
                                                onClick={() => setShopMenu(false)}
                                                className="block px-3 py-2 text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] hover:bg-[var(--tb-bg-2)] transition"
                                                data-testid={`shop-cat-${c.category}`}>
                                                {c.label}
                                            </Link>
                                        ))}
                                    </div>
                                    <div>
                                        <Link
                                            to="/tienda"
                                            onClick={() => setShopMenu(false)}
                                            className="block p-3 border-b border-[var(--tb-border)] text-sm font-display text-[var(--tb-gold)] hover:bg-[var(--tb-bg-2)]"
                                            data-testid="shop-cat-men">
                                            {t("♂ Hombres", "♂ Men")}
                                        </Link>
                                        {FEATURED_CATEGORIES.men.map((c) => (
                                            <Link
                                                key={c.category}
                                                to={`/tienda?categoria=${encodeURIComponent(c.category)}`}
                                                onClick={() => setShopMenu(false)}
                                                className="block px-3 py-2 text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] hover:bg-[var(--tb-bg-2)] transition"
                                                data-testid={`shop-cat-${c.category}`}>
                                                {c.label}
                                            </Link>
                                        ))}
                                    </div>
                                    <Link
                                        to="/tienda"
                                        onClick={() => setShopMenu(false)}
                                        className="col-span-2 block p-3 text-center text-xs uppercase tracking-wider border-t border-[var(--tb-border-gold)]/40 bg-[var(--tb-bg-2)] hover:bg-[var(--tb-bg)] hover:text-[var(--tb-gold)] transition"
                                        data-testid="shop-view-all">
                                        {t("Ver toda la tienda →", "Shop everything →")}
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                    {links.slice(1).map((l) => (
                        <Link key={l.to} to={l.to}
                            className={`tb-link text-sm tracking-wide whitespace-nowrap ${loc.pathname === l.to ? "text-[var(--tb-gold)]" : ""}`}
                            data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}>
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
                    <Link to="/carrito" className="relative tb-link" data-testid="nav-cart">
                        <ShoppingBag className="w-5 h-5" />
                        {count > 0 && (
                            <span className="absolute -top-2 -right-2 bg-[var(--tb-gold)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{count}</span>
                        )}
                    </Link>
                    {user ? (
                        <div className="hidden xl:flex items-center gap-3 min-w-0">
                            <Link to={user.role === "admin" ? "/admin" : user.role === "vendedor" ? "/vendedor" : "/perfil"}
                                className="flex items-center gap-2 tb-link min-w-0 max-w-[160px] 2xl:max-w-[240px]"
                                title={user.name}
                                data-testid="nav-profile">
                                <User className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm flex items-center gap-1 min-w-0">
                                    <span className="truncate">{getPublicName(user)}</span>
                                    {user.verified && <BadgeCheck className="w-4 h-4 text-[#2196F3] fill-[#2196F3]/20 flex-shrink-0" />}
                                </span>
                            </Link>
                            <button onClick={() => { logout(); nav("/"); }} className="tb-link flex-shrink-0" data-testid="nav-logout">
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <Link to="/login" className="tb-btn-secondary hidden xl:inline-block" data-testid="nav-login">{t("Acceder", "Sign in")}</Link>
                    )}
                    <button className="xl:hidden tb-link" onClick={() => setOpen(!open)} data-testid="nav-toggle">
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="xl:hidden border-t border-[var(--tb-border)] px-6 py-4 space-y-3 bg-[var(--tb-surface-2)]">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block tb-link" data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}>{l.label}</Link>
                    ))}
                    {/* Tienda (with quick-access categories) — mobile */}
                    <div>
                        <Link to="/tienda" onClick={() => setOpen(false)} className="block tb-link font-display text-[var(--tb-gold)]" data-testid="nav-mobile-tienda">{t("Tienda", "Shop")} →</Link>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 pl-3 border-l-2 border-[var(--tb-border-gold)]/40">
                            <div className="text-[10px] text-[var(--tb-muted)] uppercase tracking-widest col-span-2 mt-1">{t("♀ Mujeres", "♀ Women")}</div>
                            {FEATURED_CATEGORIES.women.map((c) => (
                                <Link key={c.category} to={`/tienda?categoria=${encodeURIComponent(c.category)}`}
                                    onClick={() => setOpen(false)}
                                    className="block text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] py-1"
                                    data-testid={`nav-mobile-cat-${c.category}`}>
                                    {c.label}
                                </Link>
                            ))}
                            <div className="text-[10px] text-[var(--tb-muted)] uppercase tracking-widest col-span-2 mt-2">{t("♂ Hombres", "♂ Men")}</div>
                            {FEATURED_CATEGORIES.men.map((c) => (
                                <Link key={c.category} to={`/tienda?categoria=${encodeURIComponent(c.category)}`}
                                    onClick={() => setOpen(false)}
                                    className="block text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] py-1"
                                    data-testid={`nav-mobile-cat-${c.category}`}>
                                    {c.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                    {user ? (
                        <>
                            <Link to={user.role === "admin" ? "/admin" : user.role === "vendedor" ? "/vendedor" : "/perfil"} className="block tb-link" onClick={() => setOpen(false)}>
                                {user.role === "admin" ? t("Admin", "Admin") : user.role === "vendedor" ? t("Panel Vendedor", "Seller Panel") : t("Mi Perfil", "My Profile")}
                            </Link>
                            <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block tb-link">{t("Cerrar sesión", "Sign out")}</button>
                        </>
                    ) : (
                        <Link to="/login" className="block tb-link" onClick={() => setOpen(false)}>{t("Acceder", "Sign in")}</Link>
                    )}
                </div>
            )}
        </header>
    );
}
